import { type ComponentProps, isValidElement, useEffect, useRef, useState } from "react";

import { childrenToText, highlightCode } from "../lib/highlight.js";
import { useLocaleCode } from "../lib/i18n.jsx";
import { aplicarTokens } from "../lib/runtime-config.js";
import Icon from "./Icon.jsx";
import Mermaid from "./Mermaid.jsx";

// Substitui o <pre> do MDX. O texto vem de dentro do <code> filho, junto com a
// className "language-*" que o remark coloca a partir da cerca ```json.
export default function CodeBlock({ children, className = "", ...rest }: ComponentProps<"pre">) {
  const locale = useLocaleCode();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const codeEl = Array.isArray(children) ? children[0] : children;
  const codeProps: { className?: string } = isValidElement<{ className?: string }>(codeEl) ? codeEl.props : {};
  // `{SITE_URL}` e `{MCP_URL}` escritos no .mdx viram a URL do ambiente. Feito
  // aqui, e não no MDX, porque cerca de código é literal: dentro dela nenhum
  // componente renderiza. Como o texto destacado e o texto copiado saem os dois
  // daqui, o botão de copiar entrega a URL real, não o placeholder.
  const text = aplicarTokens(childrenToText(children), locale);
  const lang = (codeProps.className || "").replace(/^language-/, "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard bloqueado (http, permissão negada): o bloco continua
      // selecionável na mão, então não vale interromper a leitura com um erro.
    }
  };

  if (!text)
    return (
      <pre className={`doc-code ${className}`} {...rest}>
        {children}
      </pre>
    );

  // ```mermaid vira diagrama em vez de bloco de código, para que um fluxograma
  // simples possa ser escrito direto no .mdx sem importar componente nenhum.
  if (lang === "mermaid") return <Mermaid chart={text} />;

  return (
    <div className="doc-code-wrap">
      {lang && <span className="doc-code-lang mono">{lang}</span>}
      <button type="button" className="doc-code-copy" onClick={copy} aria-label={copied ? "Copiado" : "Copiar código"}>
        <Icon name={copied ? "check" : "copy"} size={13} />
      </button>
      <pre className={`doc-code ${className}`} {...rest}>
        <code dangerouslySetInnerHTML={{ __html: highlightCode(text) }} />
      </pre>
    </div>
  );
}
